import React, { useEffect, useState } from "react";
import "./Products.css";
import { useAdminAuth } from "../../context/AdminAuthContext";
import ProductCard from "../../components/ListproductsCard/ProductCard";
// import toast from "react-hot-toast";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const Products = () => {
  const { addClothingProduct } = useAdminAuth();
  const { products, nextPage, fetchProducts, name, setName, page, setPage } = useAdminAuth();
  const { categories } = useAdminAuth();
  // Admin-managed clothing types (Shirt / Hoodie / T-shirt / ...). Only the
  // active ones are offered here, but a product already saved with a type
  // that has since been switched off keeps showing it (see below).
  const { garmentTypes } = useAdminAuth();
  
  const initialFormState = {
    _id: null,
    name: "",
    description: "",
    category: "",
    subCategory: "",
    // Garment type: the plain word for the item — Shirt, Hoodie, T-shirt.
    // Not the same thing as category, which is the collection name.
    productType: "",
    // "" means not set, which the storefront shows under BOTH Men and Women,
    // exactly like unisex. Both are left optional on purpose.
    gender: "",
    brand: "",
    price: "",
    stock: [{ size: "S", quantity: "", color: "" }],
    images: [],
    filterTags: [],
    attributes: {
      color: [],
      fabricType: "",
      careInstructions: "",
      material: "",
      pattern: "",
    },
    visibility: "public",
    colorGroup: "",
    // Per-colourway groups: [{ color, group }]. The product-level `colorGroup`
    // above stays as the fallback for any colour left unset.
    colorGroups: [],
    washCare: "",
    shipping: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [currentTag, setCurrentTag] = useState("");
  const [imagePreviews, setImagePreviews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [modalMode, setModalMode] = useState("create");
  const [productToEdit, setProductToEdit] = useState(null);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviews]);

  const openCreateModal = () => {
    setModalMode("create");
    setProductToEdit(null);
    setFormData(initialFormState);
    setImagePreviews([]);
    setShowModal(true);
  };

  const openUpdateModal = async (productId) => {
    setModalMode("update");
    setProductToEdit(productId);
    setImagePreviews([]);
    
    try {
      const token = localStorage.getItem("admintoken");
      const response = await fetch(
        `https://admin.riymay.com/api/v1/admin/get-clothing-product?productid=${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Products saved before per-colour groups existed have no colorGroups
        // field at all. Normalise to [] so the form has something to render.
        // Products saved before garment type / gender existed have neither
        // field. Normalise to "" so the selects are controlled inputs and an
        // untouched product posts "" back, i.e. still not set.
        setFormData({
          ...data,
          colorGroups: Array.isArray(data.colorGroups) ? data.colorGroups : [],
          productType: data.productType || "",
          gender: data.gender || "",
        });
        // Create image previews from existing images
        if (data.images && Array.isArray(data.images)) {
          const previews = data.images.map(img => `https://riymay.com/assets/${img.url}`);
          setImagePreviews(previews);
        }
        setSubCategories([data.subCategory]);
        setShowModal(true);
        toast.success("Product fetched successfully!");
      } else {
        const errorData = await response.json();
        toast.error(`Failed to fetch product: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Error fetching product details");
    }
  };

  const handleUpdate = async (productId, updatedData) => {
    try {
      const token = localStorage.getItem("admintoken");
      const response = await fetch(
        `https://admin.riymay.com/api/v1/admin/update-clothing-product?productId=${productId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: updatedData,
        }
      );

      if (response.ok) {
        toast.success("Product updated successfully!");
        fetchProducts(page);
        setShowModal(false);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to update product: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("Error updating product");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("admintoken");
      const response = await fetch(
        `https://admin.riymay.com/api/v1/admin/delete-clothing-product?productid=${productId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        toast.success("Product deleted successfully!");
        fetchProducts(page);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete product: ${errorData.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Error deleting product");
    }
  };

  useEffect(() => {
    fetchProducts(page);
  }, [page]);

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    setPage(page + 1);
  };

  const handleTagInput = (e) => {
    if (e.key === "Enter" && currentTag.trim()) {
      e.preventDefault();
      setFormData({
        ...formData,
        filterTags: [...formData.filterTags, currentTag.trim()],
      });
      setCurrentTag("");
    }
  };

  const removeTag = (indexToRemove) => {
    setFormData({
      ...formData,
      filterTags: formData.filterTags.filter((_, index) => index !== indexToRemove),
    });
  };

  const handleMainCategoryChange = (e) => {
    const selectedName = e.target.value;
    setSelectedCategory(selectedName);

    const category = categories.find((item) => item.name === selectedName);
    setSubCategories(category ? category.subCategories : []);

    setFormData({
      ...formData,
      category: selectedName,
    });
  };

  const handleSubcategory = (e) => {
    setFormData({
      ...formData,
      subCategory: e.target.value,
    });
  };

  const handleAttributesChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      attributes: { ...formData.attributes, [name]: value },
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStockChange = (index, field, value) => {
    const updatedStock = [...formData.stock];
    updatedStock[index][field] = value;
    setFormData({ ...formData, stock: updatedStock });
  };

  const addStockField = () => {
    setFormData({
      ...formData,
      stock: [...formData.stock, { size: "", quantity: "", color: "" }],
    });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // COLOUR GROUP PER COLOUR
  //
  // The colours of a product are whatever colours were typed into the stock
  // rows, de-duplicated (a colour appears once per size, so "rust" shows up in
  // three rows for a 3-size product but is ONE colourway). The owner picks a
  // group for each colourway; anything left blank falls back to the
  // product-level Colour Group below.
  // ───────────────────────────────────────────────────────────────────────────
  const distinctColors = (() => {
    const seen = new Set();
    const out = [];
    (formData.stock || []).forEach((row) => {
      const color = (row.color || "").trim();
      if (!color) return;
      const key = color.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(color);
    });
    return out;
  })();

  const getColorGroup = (color) => {
    const match = (formData.colorGroups || []).find(
      (g) => (g.color || "").toLowerCase() === color.toLowerCase()
    );
    return match ? match.group : "";
  };

  const handleColorGroupChange = (color, group) => {
    const rest = (formData.colorGroups || []).filter(
      (g) => (g.color || "").toLowerCase() !== color.toLowerCase()
    );
    const next = group ? [...rest, { color, group }] : rest;
    setFormData({ ...formData, colorGroups: next });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imagePreviews.length > 8) {
      toast.error("You can upload a maximum of 8 images total.");
      return;
    }
    
    // Add new files to the existing images array
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
    
    // Create and add new preview URLs
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleDeleteProductImage = async (src, index, productId) => {
    try {
      // Extract the image URL from the full path
      const imageUrl = src.replace("https://riymay.com/assets/", "");
      
      const token = localStorage.getItem("admintoken");
      const response = await fetch(
        `https://admin.riymay.com/api/v1/admin/delete/product/image`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ url: imageUrl, index, productId }),
        }
      );
      
      if (response.ok) {
        toast.success("Image deleted successfully!");
        return true;
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete image: ${errorData.message || "Unknown error"}`);
        return false;
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Error deleting image");
      return false;
    }
  };

  const removeImagePreview = async (index) => {
    // Check if we're in update mode and dealing with an existing image
    if (modalMode === "update" && productToEdit && index < imagePreviews.length) {
      const imageUrl = imagePreviews[index];
      
      // Only call the API if it's an existing image (not a new upload)
      if (imageUrl.includes("riymay.com/assets")) {
        const success = await handleDeleteProductImage(imageUrl, index, productToEdit);
        if (!success) return; // Don't proceed if deletion failed
      }
    }
    
    // Remove the preview
    const newPreviews = [...imagePreviews];
    newPreviews.splice(index, 1);
    setImagePreviews(newPreviews);
    
    // Remove from formData.images
    const newImages = [...formData.images];
    if (index < newImages.length) {
      newImages.splice(index, 1);
      setFormData(prev => ({ ...prev, images: newImages }));
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   // Validate form
  //   if (!formData.name || !formData.price) {
  //     toast.error("Product name and price are required!");
  //     return;
  //   }

  //   // Prepare FormData
  //   const productInfo = new FormData();
    
  //   Object.entries(formData).forEach(([key, value]) => {
  //     if (key === "images") {
  //       // Only append File objects (new uploads)
  //       value.forEach((file, index) => {
  //         if (file instanceof File) {
  //           productInfo.append(`images[${index}]`, file);
  //         }
  //       });
  //     } else if (key === "filterTags") {
  //       value.forEach((tag) => productInfo.append("filterTags[]", tag));
  //     } else if (key === "stock") {
  //       value.forEach((item) => {
  //         productInfo.append("stock[]", JSON.stringify(item));
  //       });
  //     } else if (key === "attributes") {
  //       productInfo.append("attributes", JSON.stringify(value));
  //     } else {
  //       productInfo.append(key, value);
  //     }
  //   });

  //   try {
  //     if (modalMode === "create") {
  //       await addClothingProduct(productInfo);
  //       toast.success("Product created successfully!");
  //       setShowModal(false);
  //     } else if (modalMode === "update" && productToEdit) {
  //       await handleUpdate(productToEdit, productInfo);
  //     }
      
  //     // Reset form and close modal
  //     setFormData(initialFormState);
  //     setImagePreviews([]);
  //     fetchProducts(page);
  //   } catch (error) {
  //     console.error(`Error ${modalMode === "create" ? "creating" : "updating"} product:`, error);
  //     toast.error(`Failed to ${modalMode === "create" ? "create" : "update"} product`);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting form in mode:", modalMode);
  
    // Validate form - allow price to be 0
    if (!formData.name || formData.price === undefined || formData.price === null) {
      toast.error("Product name and price are required!");
      return;
    }

    // Every colourway must end up in a group, so the Warms / Cools / Neutrals
    // collections in the site nav always resolve to real products. A colour
    // with its own group is covered; anything left blank needs the
    // product-level fallback to be set.
    const groupedColorGroups = distinctColors
      .map((color) => ({ color, group: getColorGroup(color) }))
      .filter((entry) => entry.group);

    const ungrouped = distinctColors.filter((color) => !getColorGroup(color));

    if (!formData.colorGroup && ungrouped.length > 0) {
      toast.error(
        distinctColors.length > 0
          ? `Choose a colour group for: ${ungrouped.join(", ")} — or set the product-level Colour Group as the fallback.`
          : "Please choose a Colour Group — Warms, Cools or Neutrals."
      );
      return;
    }
  
    // Prepare FormData
    const productInfo = new FormData();
    
    // Add all form data to FormData object
    productInfo.append("name", formData.name);
    productInfo.append("description", formData.description);
    productInfo.append("category", formData.category);
    productInfo.append("subCategory", formData.subCategory || ""); // Ensure subCategory is never undefined
    productInfo.append("brand", formData.brand || ""); // Ensure brand is never undefined
    productInfo.append("price", formData.price.toString()); // Ensure price is string
    productInfo.append("visibility", formData.visibility);
    productInfo.append("colorGroup", formData.colorGroup || "");
    // Sent as ONE JSON field. The backend normalises it, drops unknown groups
    // and mirrors the first one into the product-level colorGroup so the
    // storefront's existing single-group filter still finds this product.
    productInfo.append("colorGroups", JSON.stringify(groupedColorGroups));
    // Appended for BOTH create and update. The create controller destructures
    // a fixed list of fields out of req.body, so a field posted here but not
    // read there is silently discarded — that is what happened to washCare
    // and shipping. Both of these are read on create and on update.
    productInfo.append("productType", formData.productType || "");
    productInfo.append("gender", formData.gender || "");
    productInfo.append("washCare", formData.washCare || "");
    productInfo.append("shipping", formData.shipping || "");
    
    // Handle arrays and nested objects
    formData.filterTags.forEach(tag => productInfo.append("filterTags[]", tag));
    
    // Handle stock items - ensure proper formatting
    formData.stock.forEach((item, index) => {
      const stockItem = {
        size: item.size,
        quantity: item.quantity,
        color: item.color,
        stockStatus: item.stockStatus || "inStock" // Default status if not provided
      };
      productInfo.append(`stock[${index}]`, JSON.stringify(stockItem));
    });
    
    productInfo.append("attributes", JSON.stringify(formData.attributes));
    
    // Handle images - only append new files and ensure empty array is handled
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file, index) => {
        if (file instanceof File) {
          productInfo.append(`images[${index}]`, file);
        }
      });
    } else if (modalMode === "create") {
      // For new products, require at least one image
      toast.error("At least one image is required for new products!");
      return;
    }
  
    try {
      if (modalMode === "create") {
        console.log("Creating new product");
        await addClothingProduct(productInfo);
        toast.success("Product created successfully!");
        setShowModal(false);
      } else if (modalMode === "update" && productToEdit) {
        console.log("Updating existing product with ID:", productToEdit);
        console.log("Data being sent:", Object.fromEntries(productInfo.entries()));
        
        await handleUpdate(productToEdit, productInfo);
      }
      
      // Reset form and close modal
      setFormData(initialFormState);
      setImagePreviews([]);
      fetchProducts(page);
    } catch (error) {
      console.error(`Error ${modalMode === "create" ? "creating" : "updating"} product:`, error);
      toast.error(`Failed to ${modalMode === "create" ? "create" : "update"} product: ${error.message}`);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setImagePreviews([]);
    setFormData(initialFormState);
  };

  return (
    <div className="table-container">
      <div className="search-bar-container">
        <h1>Products Management</h1>

        <input
          type="text"
          name="name"
          placeholder="Search by name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="search-bar"
        />
        <button
          className="pincode-submit-btn"
          onClick={openCreateModal}
        >
          Create Product +
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>
              {modalMode === "create" ? "Create Product" : "Update Product"}
            </h2>
            <form className="form-container" onSubmit={handleSubmit}>
              <label>Product Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-input"
                required
              />

              <label>Description:</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="text-area-description"
                rows="10"
                cols="11"
                required
              />

              <div>
                <h4>Category Selector</h4>
                <div className="category-Selector">
                  <label htmlFor="mainCategory">Main Category: </label>
                  <select
                    onChange={handleMainCategoryChange}
                    id="mainCategory"
                    value={formData.category}
                    required
                  >
                    <option value="" disabled>
                      Select a category
                    </option>
                    {categories.map((item) => (
                      <option key={item._id} value={item.name}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  
                  {subCategories.length > 0 && (
                    <div>
                      <label htmlFor="subCategory">Subcategories: </label>
                      <select 
                        onChange={handleSubcategory} 
                        id="subCategory"
                        value={formData.subCategory}
                      >
                        <option value="" disabled>
                          Select a subcategory
                        </option>
                        {subCategories.map((sub, index) => (
                          <option key={index} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="attributes-section">
                <label htmlFor="productType">Garment Type:</label>
                <p className="color-group-hint">
                  What the item actually is — Shirt, Hoodie, T-shirt, Shacket,
                  Pant. This is what shoppers filter by. Manage the list on the
                  Garment Types page.
                </p>
                <select
                  id="productType"
                  name="productType"
                  value={formData.productType || ""}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">— Not set —</option>
                  {/* Active types, plus whatever this product already has even
                      if that type has since been switched off or deleted — so
                      editing an old product never silently changes its type. */}
                  {garmentTypes
                    .filter(
                      (type) =>
                        type.active || type.name === formData.productType
                    )
                    .map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                        {type.active ? "" : " (hidden from filters)"}
                      </option>
                    ))}
                  {formData.productType &&
                    !garmentTypes.some(
                      (type) => type.name === formData.productType
                    ) && (
                      <option value={formData.productType}>
                        {formData.productType} (no longer in the list)
                      </option>
                    )}
                </select>
              </div>

              <div className="attributes-section">
                <label htmlFor="gender">Men / Women:</label>
                <p className="color-group-hint">
                  Which tab this shows under. Unisex shows under both. Leaving
                  it unset also shows it under both.
                </p>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender || ""}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">— Not set (shows in both) —</option>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="unisex">Unisex (shows in both)</option>
                </select>
              </div>

              <div className="tags-section">
                <label>Filtered Tags:</label>
                <div className="tags-input-container">
                  <input
                    type="text"
                    placeholder="Type a tag and press Enter"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyDown={handleTagInput}
                    className="form-input"
                  />
                </div>
                <div className="tags-display">
                  {formData.filterTags.map((tag, index) => (
                    <div key={index} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="remove-tag-button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <label>Brand:</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                className="form-input"
              />

              <label>Price:</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="form-input"
                required
              />

              <div className="stock-fields">
                <label>Stock:</label>
                {formData.stock.map((stock, index) => (
                  <div key={index} className="stock-row">
                    <select
                      value={stock.size}
                      onChange={(e) =>
                        handleStockChange(index, "size", e.target.value)
                      }
                      className="form-input"
                      required
                    >
                      <option value="" disabled>Select size</option>
                      {[
                        "oneSize",
                        "XS",
                        "S",
                        "M",
                        "L",
                        "XL",
                        "XXL",
                        "28",
                        "30",
                        "32",
                        "34",
                        "36",
                        "38",
                        "40",
                        "42",
                        "44",
                        "46",
                      ].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={stock.quantity}
                      onChange={(e) =>
                        handleStockChange(index, "quantity", e.target.value)
                      }
                      className="form-input stock-quantity"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Product color"
                      value={stock.color}
                      onChange={(e) =>
                        handleStockChange(index, "color", e.target.value)
                      }
                      className="form-input stock-quantity"
                      required
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStockField}
                  className="add-stock-button"
                >
                  Add Stock Field
                </button>
              </div>

              {/* Sits directly under the stock rows on purpose. It builds
                  itself from the colours typed into those rows, and when it
                  lived 160 lines further down the owner could not find it --
                  he reported only ever seeing the single fallback dropdown
                  and thought per-colour groups were missing entirely. */}
              <div className="attributes-section">
                <label>Colour Group per Colour (Collections):</label>
                <p className="color-group-hint">
                  One row per colour, taken from the stock rows above. Each
                  colour goes into its own collection — the rust hoodie in
                  Warms, the slate one in Cools — rather than the whole
                  product being forced into a single group.
                </p>

                {distinctColors.length === 0 ? (
                  <p className="color-group-hint">
                    Add a stock row above with a colour in it, and that colour
                    appears here with its own collection. A hoodie in seven
                    colours gets seven rows here — one per colour.
                  </p>
                ) : (
                  <div className="color-group-rows">
                    {distinctColors.map((color) => (
                      <div className="color-group-row" key={color.toLowerCase()}>
                        <span className="color-group-name">{color}</span>
                        <select
                          value={getColorGroup(color)}
                          onChange={(e) => handleColorGroupChange(color, e.target.value)}
                          className="form-input"
                        >
                          <option value="">— Use product default below —</option>
                          <option value="warms">Warms</option>
                          <option value="cools">Cools</option>
                          <option value="neutrals">Neutrals</option>
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="attributes-section">
                <label>Color:</label>
                <input
                  type="text"
                  name="color"
                  value={formData.attributes.color}
                  onChange={handleAttributesChange}
                  className="form-input"
                />
              </div>

              <div className="attributes-section">
                <label>Fabric Type:</label>
                <input
                  type="text"
                  name="fabricType"
                  value={formData.attributes.fabricType}
                  onChange={handleAttributesChange}
                  className="form-input"
                />
              </div>

              <div className="attributes-section">
                <label>Care Instructions:</label>
                <input
                  type="text"
                  name="careInstructions"
                  value={formData.attributes.careInstructions}
                  onChange={handleAttributesChange}
                  className="form-input"
                />
              </div>

              <div className="attributes-section">
                <label>Material:</label>
                <input
                  type="text"
                  name="material"
                  value={formData.attributes.material}
                  onChange={handleAttributesChange}
                  className="form-input"
                />
              </div>

              <div className="attributes-section">
                <label>Pattern:</label>
                <input
                  type="text"
                  name="pattern"
                  value={formData.attributes.pattern}
                  onChange={handleAttributesChange}
                  className="form-input"
                />
              </div>

              <div className="attributes-section">
                <label>Visibility:</label>
                {modalMode === "update" && <p className="product-id">Product ID: {formData._id}</p>}
                <select
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div className="attributes-section">
                <label>Wash Care:</label>
                <textarea
                  name="washCare"
                  value={formData.washCare}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                  placeholder="e.g. Machine wash cold, tumble dry low, do not bleach"
                />
              </div>

              <div className="attributes-section">
                <label>Shipping Details:</label>
                <textarea
                  name="shipping"
                  value={formData.shipping}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                  placeholder="e.g. Dispatched in 1-2 days. Delivery 4-5 business days."
                />
              </div>


              <div className="attributes-section">
                <label>Product Colour Group (fallback for colours left unset):</label>
                <select
                  name="colorGroup"
                  value={formData.colorGroup || ""}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">— Select Warms, Cools or Neutrals —</option>
                  <option value="warms">Warms</option>
                  <option value="cools">Cools</option>
                  <option value="neutrals">Neutrals</option>
                </select>
              </div>

              <label>Upload Product Images:</label>
              <input
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={handleImageUpload}
                className="form-input"
              />
              
              {imagePreviews.length > 0 && (
                <div className="image-previews">
                  {imagePreviews.map((src, index) => (
                    <div key={index} className="image-preview-container">
                      <img
                        src={src}
                        alt={`Preview ${index + 1}`}
                        className="image-preview"
                      />
                      <button
                        type="button"
                        onClick={() => removeImagePreview(index)}
                        className="remove-image-button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button type="submit" className="submit-update-button">
                  {modalMode === "create" ? "Create" : "Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <div className="products-pagination">
        <div className="product-list">
          {products.length > 0 ? (
            products.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onUpdate={openUpdateModal}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <p className="no-products">No products found</p>
          )}
        </div>
        
        <div className="pagination-controls">
          <button onClick={handlePrevious} disabled={page === 1}>
            Previous
          </button>
          <span>Page {page}</span>
          <button onClick={handleNext} disabled={!nextPage}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Products;
